import { IntlShape } from "react-intl";
import { authorizedUpsells } from "@Components/molecules/customization/Upsells/whiteLists";
import { CheerzApiCustomizationProductOption, SelectedUpsell } from "@Types/api/customization";
import { Currency } from "@Types/api/price";
import { Upsell, UpsellIdentifiers, UpsellWhitelistedProductOption } from "@Types/upsells";

export const getTotalPrice = (
  upsells: Upsell[],
  selectedUpsellsTags: Array<{ tag: string; value: string }>
): number => {
  const price = upsells
    .map((upsell) => {
      return selectedUpsellsTags.find(
        (tags) => tags.tag === upsell.tag && tags.value === upsell.value
      )
        ? upsell.price.cents
        : 0;
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue);
  return price;
};

export const getFormattedTotalPrice = (
  upsells: Upsell[],
  selectedUpsellsTags: Array<{ tag: string; value: string }>,
  intl: IntlShape
): string => {
  const currency = upsells[0].price.currency;
  const price = getTotalPrice(upsells, selectedUpsellsTags);
  const formattedPrice = intl.formatNumber(price, {
    currency,
    style: "currency",
  });
  return formattedPrice;
};

export const isSelected = (
  tag: string,
  value: string,
  selectedUpsellsTags: UpsellIdentifiers[]
): boolean => {
  return selectedUpsellsTags.findIndex(
    (upsellTags) => upsellTags.tag === tag && upsellTags.value === value
  ) >= 0
    ? true
    : false;
};

export const formatSelectedOptions = (
  selectedUpsells: UpsellIdentifiers[],
  productOptions: CheerzApiCustomizationProductOption[] | undefined
): SelectedUpsell[] | undefined => {
  if (productOptions) {
    const productOptionsTags = productOptions.map((productOption) => productOption.tag);
    return selectedUpsells
      .filter((upsellProduct) => productOptionsTags.includes(upsellProduct.tag))
      .map((option) => {
        return {
          quantity: 1,
          tag: option.tag,
          value: option.value,
        };
      });
  }
};

export const convertFromApiToUpsells = (
  productOptions: CheerzApiCustomizationProductOption[],
  intl: IntlShape,
  currency: Currency
): Upsell[] => {
  const authorizedUpsellsTags = authorizedUpsells.map((upsell) => upsell.tag);

  const flattenedOptions = productOptions.reduce((reducedOptions, productOption) => {
    if (
      productOption.option_values.length === 0 ||
      (productOption.option_values.length === 1 &&
        authorizedUpsellsTags.includes(productOption.tag) === false)
    ) {
      // exception for case like packaging -> [box-20x20] where box-20x20 is the expected value
      if (
        productOption.option_values.length === 1 &&
        authorizedUpsellsTags.includes(productOption.option_values[0].tag)
      ) {
        return [
          ...reducedOptions,
          { ...productOption, option_values: [], value: productOption.option_values[0] },
        ];
      }

      return reducedOptions;
    }

    if (productOption.option_values.length > 1) {
      const includedOptions = productOption.option_values
        .filter((optionValue) => authorizedUpsellsTags.includes(optionValue.tag))
        .map((optionValue) => {
          return { ...productOption, option_values: [], value: optionValue };
        });
      return [...reducedOptions, ...includedOptions];
    } else {
      return [...reducedOptions, productOption];
    }
  }, [] as UpsellWhitelistedProductOption[]);

  return flattenedOptions.map((option) => {
    const optionRefTag = option.value ? option.value.tag : option.tag;
    const authorizedUpsell = authorizedUpsells.find((upsell) => upsell.tag === optionRefTag);
    const imageUrl = authorizedUpsell ? authorizedUpsell.imageUrl : "";

    return {
      description: intl.formatMessage({
        id: `cheerz_UpsellsModal_description__${optionRefTag}`,
      }),
      imageUrl,
      price: {
        cents: option.value ? option.value.price : option.option_values[0].price,
        currency,
      },
      tag: option.tag,
      title: intl.formatMessage({ id: `cheerz_UpsellsModal_title__${optionRefTag}` }),
      value: option.value ? option.value.tag : option.option_values[0].tag,
    };
  });
};
